import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { BASEPATH } from "./lib/redirect";
import { Loading } from "./components/ui";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or a 403 is an answer, not a blip. Retrying one three times just
      // makes the redirect to /login take a second and a half.
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  // Matches vite's `base` and Caddy's route. The router strips it before
  // matching, so every `to=` in the app is written without it.
  basepath: BASEPATH,
  context: { queryClient },
  defaultPreload: "intent",
  // Loaders read through React Query, which owns the staleness decision. Left
  // at its default the router would serve its own stale copy on a preload and
  // the two caches would disagree.
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => <Loading />,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("app")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
